import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { Layout } from 'app/pages/NewAccount/layouts';
import { Text, Title } from '../../components/Typo';
import StyledButton from 'app/components/StyledButton';
import { ACCOUNT_MNG_MENU } from 'app/pages/NewAccount/layouts/AccountManager';
import { LoadingContainer } from 'app/components/LoadingContainer';
import {
  approveOrderRefundTicketsApi,
  cancelOrderRefundTicketsApi,
  getOrderReturnTicketsApi,
} from 'app/services/checkTicketApi';
import { formatNumber } from 'utils/helper';
import { CANCEL_REASONS } from '../TicketSelectedList';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { SESSION_MESSAGE } from 'utils/constants';
import CancellationList from './components/CancellationList';
import GiftItems from './components/GiftItems';
import useWindowSize from 'app/hooks/useWindowSize';

export enum ORDER_REFUND_STATUS {
  PENDING_APPROVE_STATUS = 'pendingApprove',
  APPROVED_STATUS = 'approved',
  INIT_STATUS = 'init',
  SUCCESS_STATUS = 'success',
  CANCELLED = 'cancelled',
}

export enum CANCEL_ORDER_ERROR_CODE {
  CANCELLED = 'CANCELLED',
  APPROVED = 'APPROVED',
}

const TicketRefundDetail = () => {
  const translations = useSelector(selectTranslations);
  const { idHash } = useParams<any>();
  const { state } = useLocation<any>();
  const originPage = state?.originPage;
  const [orderRefundData, setOrderRefundData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const keepLoading = useRef(false);
  const history = useHistory();
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  const { executeRecaptcha } = useGoogleReCaptcha();
  const infoUser = useSelector(selectInfoUser);

  const cancellationListData = orderRefundData?.tickets;

  const giftItems = orderRefundData?.gift_tickets;

  const isPolicyOrder = useMemo(() => {
    return (
      +orderRefundData?.order?.returnPolicy ===
      CANCEL_REASONS.ACCORDING_TO_POLICY
    );
  }, [orderRefundData?.order?.returnPolicy]);

  const orderStatus = useMemo(
    () => orderRefundData?.order?.orderState?.value,
    [orderRefundData?.order?.orderState?.value],
  );

  const orderStatusText = useMemo(() => {
    if (isPolicyOrder) return '';
    switch (orderStatus) {
      case ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS:
        return translations?.['b2b.txt.refundOrderPendingApprove'] ?? '';
      case ORDER_REFUND_STATUS.APPROVED_STATUS:
        return translations?.['b2b.txt.refundOrderWaitConfirm'] ?? '';
      default:
        return '';
    }
  }, [isPolicyOrder, orderStatus, translations]);

  const isDisplayApproveReason =
    !isPolicyOrder &&
    [
      ORDER_REFUND_STATUS.CANCELLED,
      ORDER_REFUND_STATUS.SUCCESS_STATUS,
      ORDER_REFUND_STATUS.APPROVED_STATUS,
    ].includes(orderStatus);

  const onCancelOrder = useCallback(
    async ({
      onBeforeGetCaptcha,
      onBefore,
      onSuccess,
      onError,
      onFinally,
      forceCancel,
    }: {
      onBeforeGetCaptcha?: () => boolean;
      onBefore?: () => void;
      onSuccess?: (data) => void;
      onError?: (error) => void;
      onFinally?: () => void;
      forceCancel?: true;
    }) => {
      if (!executeRecaptcha || (onBeforeGetCaptcha && !onBeforeGetCaptcha()))
        return;
      const formData = new FormData();
      const tokenCaptcha = await executeRecaptcha('cancelRefundTicketOrder');
      formData.append('idHash', idHash);
      formData.append('captcha', tokenCaptcha);
      if (forceCancel) formData.append('force', 'true');
      try {
        onBefore?.();
        const res = await cancelOrderRefundTicketsApi(formData);
        onSuccess?.(res.data);
      } catch (err: any) {
        onError?.(err);
      } finally {
        onFinally?.();
      }
    },
    [executeRecaptcha, idHash],
  );

  const handleCancelOrder = (forceCancel?: true) => {
    if (isLoading) return;
    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: translations?.['b2b.txt.cfRemoveRefundTicket'] ?? '',
      buttons: [
        {
          text: translations?.['b2c.txt.back'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.btn.confirm'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: () => {
            onCloseDialogNoti();
            onCancelOrder({
              forceCancel,
              onBeforeGetCaptcha: () => {
                setIsLoading(true);
                return !isLoading;
              },
              onSuccess: () => {
                sessionStorage.setItem(
                  SESSION_MESSAGE,
                  JSON.stringify({
                    message:
                      translations?.['b2b.msg.cancelOrderSuccess']?.replace(
                        '{orderNumber}',
                        orderRefundData?.order?.id,
                      ) ?? '',
                    type: 'SUCCESS',
                  }),
                );
                history.push('/account', {
                  targetTab: 'tab-3',
                });
              },
              onError: error => {
                if (
                  [
                    CANCEL_ORDER_ERROR_CODE.CANCELLED,
                    CANCEL_ORDER_ERROR_CODE.APPROVED,
                  ].includes(error?.data?.errorCode)
                ) {
                  keepLoading.current = true;
                  loadingOrderRefundData();
                  onOpenDialogNoti({
                    type: 'ERROR',
                    isShow: true,
                    title: error?.message ?? '',
                    content: '',
                    onCloseDialog: () => {
                      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                      onCloseDialogNoti();
                    },
                    buttons: [],
                    maskClosable: false,
                    closable: true,
                  });
                }
              },
              onFinally: () => {
                if (!keepLoading.current) {
                  setIsLoading(false);
                }
              },
            });
          },
        },
      ],
      closable: false,
    });
  };

  const handleAcceptOrder = async () => {
    const formData = new FormData();
    formData.append('idHash', idHash);
    try {
      if (isLoading || !orderRefundData) return;
      setIsLoading(true);
      await approveOrderRefundTicketsApi(formData);
      onOpenDialogNoti({
        type: 'SUCCESS',
        isShow: true,
        title: '',
        content:
          translations?.['b2b.msg.orderAcceptSuccess']?.replace(
            '{orderNumber}',
            orderRefundData?.order?.id,
          ) ?? '',
        buttons: [],
        closable: true,
        maskClosable: false,
        onCloseDialog: () => {
          onCloseDialogNoti();
          history.push('/account', {
            targetTab: 'tab-1',
          });
        },
      });
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const renderActionButton = () => {
    if (
      isPolicyOrder ||
      (!isPolicyOrder && orderStatus === ORDER_REFUND_STATUS.SUCCESS_STATUS) ||
      orderStatus === ORDER_REFUND_STATUS.CANCELLED
    )
      return null;

    const isHavePermission = infoUser?.id === orderRefundData?.order?.agent?.id;

    if (orderStatus === ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS)
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            paddingTop: 24,
            borderTop: 'solid 2px #EBEBEB',
            marginTop: 24,
          }}
        >
          <StyledButton
            textColor="#808080"
            bgColor="transparent"
            border="solid 1px #808080"
            onClick={() => {
              handleCancelOrder();
            }}
            disabled={!isHavePermission}
          >
            {translations?.['b2b.txt.cancelRequest'] ?? ''}
          </StyledButton>
        </div>
      );

    if (orderStatus === ORDER_REFUND_STATUS.APPROVED_STATUS)
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            paddingTop: 24,
            borderTop: 'solid 2px #EBEBEB',
            marginTop: 24,
          }}
        >
          <StyledButton
            textColor="#808080"
            bgColor="transparent"
            border="solid 1px #808080"
            onClick={() => {
              handleCancelOrder(true);
            }}
            disabled={!isHavePermission}
          >
            {translations?.['b2b.txt.cancelRequest'] ?? ''}
          </StyledButton>
          <StyledButton
            textColor="#ffffff"
            style={{ marginLeft: 18 }}
            onClick={handleAcceptOrder}
            disabled={!isHavePermission}
          >
            {translations?.['b2b.btn.accept'] ?? ''}
          </StyledButton>
        </div>
      );
  };

  const onBack = () => {
    if (originPage) {
      if (typeof originPage === 'string') return history.push(originPage);
      return history.push(originPage.prevPage, {
        targetTab: originPage.targetTab,
      });
    }
    switch (orderRefundData?.order?.orderState?.value) {
      case ORDER_REFUND_STATUS.INIT_STATUS:
        return history.push('/account', { targetTab: 'tab-4' });
      case ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS:
      case ORDER_REFUND_STATUS.APPROVED_STATUS:
        return history.push('/account', { targetTab: 'tab-5' });
      case ORDER_REFUND_STATUS.CANCELLED:
        return history.push('/account', { targetTab: 'tab-3' });
      case ORDER_REFUND_STATUS.SUCCESS_STATUS:
      default:
        return history.push('/account', { targetTab: 'tab-1' });
    }
  };

  const loadingOrderRefundData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getOrderReturnTicketsApi(idHash);
      const resData = res.data;
      switch (resData?.order?.orderState?.value) {
        case ORDER_REFUND_STATUS.INIT_STATUS:
          history.push(`/ticket-refund/info/${idHash}`);
          break;
        case ORDER_REFUND_STATUS.APPROVED_STATUS:
        case ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS:
        case ORDER_REFUND_STATUS.SUCCESS_STATUS:
        case ORDER_REFUND_STATUS.CANCELLED:
          setOrderRefundData(resData);
          break;
        default:
          history.push('/page-not-found');
      }
    } catch (err: any) {
      history.push('/page-not-found');
    } finally {
      keepLoading.current = false;
      setIsLoading(false);
    }
  }, [history, idHash]);

  useEffect(() => {
    if (!orderRefundData && !isLoading) loadingOrderRefundData();
  }, [isLoading, loadingOrderRefundData, orderRefundData]);

  const menuActive = useMemo(() => {
    switch (originPage) {
      case '/agent/transaction-history':
        return ACCOUNT_MNG_MENU.AGENT_HISTORY;
      case typeof originPage === 'string':
        return ACCOUNT_MNG_MENU.CHECK_TICKET;
      default:
        return ACCOUNT_MNG_MENU.PURCHASE_HISTORY;
    }
  }, [originPage]);

  return (
    <Layout
      title={orderRefundData ? translations?.['b2b.order.title'] ?? '' : null}
      menuActive={menuActive}
      useBackArrow={orderRefundData}
      onBack={onBack}
    >
      <LoadingContainer loading={!orderRefundData || isLoading}>
        {orderRefundData ? (
          <div>
            <div style={{ marginTop: 10 }}>
              <Title style={{ textTransform: 'initial' }}>
                {translations?.['b2b.txt.orderInfo'] ?? ''}
              </Title>
              <div style={{ marginTop: 10 }}>
                <div
                  className="d-flex align-items-center"
                  style={{
                    padding: '8px 0',
                    // borderBottom: 'dashed 2px #D9D9D9',
                  }}
                >
                  <Text
                    style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                  >
                    {translations?.['b2b.table.PNR'] ?? ''}
                  </Text>
                  <Text style={{ fontWeight: 500 }}>
                    {orderRefundData?.order?.id}
                  </Text>
                </div>

                <div
                  className="d-flex align-items-center"
                  style={{
                    padding: '8px 0',
                    // borderBottom: 'dashed 2px #D9D9D9',
                  }}
                >
                  <Text
                    style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                  >
                    {translations?.['b2b.txt.ticketAmount'] ?? ''}
                  </Text>
                  <Text style={{ fontWeight: 500 }}>
                    {orderRefundData?.order?.totalTicket}
                  </Text>
                </div>

                <div
                  className="d-flex align-items-center"
                  style={{
                    padding: '8px 0',
                    // borderBottom: 'dashed 2px #D9D9D9',
                  }}
                >
                  <Text
                    style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                  >
                    {translations?.['b2b.txt.implementationDate'] ?? ''}
                  </Text>
                  <Text style={{ fontWeight: 500 }}>
                    {orderRefundData?.order?.orderdate?.dateDMY}
                  </Text>
                </div>

                <div
                  className="d-flex"
                  style={{
                    padding: '8px 0',
                    // borderBottom: orderStatusText
                    //   ? 'dashed 2px #D9D9D9'
                    //   : 'none',
                  }}
                >
                  <Text
                    style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                  >
                    {translations?.['b2b.txt.reasonCancellation'] ?? ''}
                  </Text>
                  <Text
                    style={{
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {orderRefundData?.order?.returnReason}
                  </Text>
                </div>

                {isDisplayApproveReason ? (
                  <div
                    className="d-flex align-items-center"
                    style={{ padding: '8px 0' }}
                  >
                    <Text
                      style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                    >
                      {translations?.['b2b.txt.reasonForApproval'] ?? ''}
                    </Text>
                    <Text style={{ fontWeight: 500 }}>
                      {orderRefundData?.order?.approveReason}
                    </Text>
                  </div>
                ) : null}

                {orderStatusText ? (
                  <div
                    className="d-flex align-items-center"
                    style={{ padding: '8px 0' }}
                  >
                    <Text
                      style={{ width: '25%', flexShrink: 0, marginRight: 10 }}
                    >
                      {translations?.['b2b.txt.order-status'] ?? ''}
                    </Text>
                    <Text style={{ fontWeight: 500 }}>{orderStatusText}</Text>
                  </div>
                ) : null}
              </div>
            </div>

            {Array.isArray(giftItems) && giftItems.length > 0 ? (
              <div style={{ marginTop: 24 }}>
                <GiftItems giftItemsData={giftItems} />
              </div>
            ) : null}

            {Array.isArray(cancellationListData) &&
            cancellationListData.length > 0 ? (
              <div style={{ marginTop: 24 }}>
                <CancellationList
                  cancellationListData={cancellationListData}
                  isPolicyOrder={isPolicyOrder}
                />
              </div>
            ) : null}

            <div style={{ marginTop: 28 }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ borderBottom: 'dashed 2px #D9D9D9', padding: '8px 0' }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.cart.totalTickets'] ?? ''}
                </Text>
                <Text style={{ fontWeight: 500 }}>
                  {formatNumber(orderRefundData?.order?.totalTicket).replaceAll(
                    ',',
                    '.',
                  )}
                </Text>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  borderBottom: 'dashed 2px #D9D9D9',
                  padding: '8px 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.txt.totalTicketPrice'] ?? ''}
                </Text>
                <Text style={{ fontWeight: 500, color: '#E92227' }}>
                  {formatNumber(orderRefundData?.order?.totalPrice).replaceAll(
                    ',',
                    '.',
                  )}
                  đ
                </Text>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  borderBottom: 'dashed 2px #D9D9D9',
                  padding: '8px 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.txt.totalFine'] ?? ''}
                </Text>
                {!isPolicyOrder &&
                orderStatus === ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS ? (
                  <Text style={{ fontWeight: 500 }}>N/A</Text>
                ) : (
                  <Text style={{ fontWeight: 500, color: '#E92227' }}>
                    {formatNumber(
                      orderRefundData?.order?.totalPenaltyFee,
                    ).replaceAll(',', '.') + 'đ'}
                  </Text>
                )}
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  padding: '8px 0 0 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                    fontSize: 16,
                  }}
                >
                  {translations?.['b2b.txt.totalRefund'] ?? ''}
                </Text>
                {!isPolicyOrder &&
                orderStatus === ORDER_REFUND_STATUS.PENDING_APPROVE_STATUS ? (
                  <Text style={{ fontWeight: 600, fontSize: 16 }}>N/A</Text>
                ) : (
                  <Text
                    style={{ fontWeight: 600, fontSize: 16, color: '#E92227' }}
                  >
                    {formatNumber(
                      orderRefundData?.order?.totalReturnCharge,
                    ).replaceAll(',', '.') + 'đ'}
                  </Text>
                )}
              </div>
            </div>

            {renderActionButton()}
          </div>
        ) : null}
      </LoadingContainer>
    </Layout>
  );
};

export default TicketRefundDetail;
